/**
 * The pattern room: toys about the structure numbers have, rather than about
 * doing sums with them.
 *
 * It exists because the number room reached ten toys and the tab strip wrapped
 * to three rows on a phone, taking the height out of the toy above it. Six
 * toys is about as many as one row of tabs can hold and still be a row a child
 * can take in at a glance.
 */

import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { CircleDot, Footprints, Network, Ruler, Sigma, TrendingUp } from 'lucide-preact';
import { Chip } from '../ui/controls.tsx';
import { FactorToy } from './math/FactorToy.tsx';
import { PrimeGridToy } from './math/PrimeGridToy.tsx';
import { SequenceToy } from './math/SequenceToy.tsx';
import { WheelToy } from './math/WheelToy.tsx';
import { SkipToy } from './math/SkipToy.tsx';
import { NumberLineToy } from './math/NumberLineToy.tsx';

type ToyId = 'pattern' | 'wheel' | 'skip' | 'factor' | 'primes' | 'line';

const TOYS: Array<{ id: ToyId; label: string; icon: JSX.Element }> = [
  { id: 'pattern', label: 'Grow', icon: <TrendingUp size={20} strokeWidth={2.75} /> },
  { id: 'wheel', label: 'Wheel', icon: <CircleDot size={20} strokeWidth={2.75} /> },
  { id: 'skip', label: 'Hop', icon: <Footprints size={20} strokeWidth={2.75} /> },
  { id: 'factor', label: 'Factor', icon: <Network size={20} strokeWidth={2.75} /> },
  { id: 'primes', label: 'Primes', icon: <Sigma size={20} strokeWidth={2.75} /> },
  { id: 'line', label: 'Line', icon: <Ruler size={20} strokeWidth={2.75} /> },
];

interface Props {
  onExit: () => void;
}

export function PatternLab({ onExit }: Props): JSX.Element {
  const [toy, setToy] = useState<ToyId>('wheel');

  const tabs = (
    <div class="dock__row dock__row--tabs" role="group" aria-label="Pattern toys">
      {TOYS.map((t) => (
        <Chip key={t.id} label={t.label} pressed={toy === t.id} onClick={() => setToy(t.id)}>
          {t.icon}
        </Chip>
      ))}
    </div>
  );

  switch (toy) {
    case 'pattern':
      return <SequenceToy onExit={onExit} tabs={tabs} />;
    case 'wheel':
      return <WheelToy onExit={onExit} tabs={tabs} />;
    case 'skip':
      return <SkipToy onExit={onExit} tabs={tabs} />;
    case 'factor':
      return <FactorToy onExit={onExit} tabs={tabs} />;
    case 'primes':
      return <PrimeGridToy onExit={onExit} tabs={tabs} />;
    case 'line':
      return <NumberLineToy onExit={onExit} tabs={tabs} />;
  }
}
